import env from "@/app/config/env";
import type {
  MarketplaceCartItemType,
  MarketplaceCartShipmentDataType,
  MarketplaceCartShipmentItemType,
  MarketplaceCartShipToType,
  MarketplaceCartType,
  ProductItemType,
} from "@/schemas/index";
import {
  MarketplaceCartSchema,
  MarketplaceCartShipmentItemSchema,
  ProductItemSchema,
} from "@/schemas/index";
import axios, { type AxiosResponse } from "axios";

export interface ServiceMarketplaceCartApiType {
  fetchMarketplaceCart: () => Promise<MarketplaceCartType>;
  fetchCartRequiredProducts: () => Promise<ProductItemType[]>;
  fetchCartRecommendedProducts: () => Promise<ProductItemType[]>;
  fetchRemoveProduct: (
    ItemId: MarketplaceCartItemType["Id"],
  ) => Promise<MarketplaceCartType>;
  fetchUpdateShoppingItemCart: (item: {
    Id: MarketplaceCartItemType["Id"];
    CostId: MarketplaceCartItemType["CostId"];
    Quantity?: number;
  }) => Promise<MarketplaceCartType>;
  fetchAddProductToCart: (
    CostId: MarketplaceCartItemType["CostId"],
    Count?: MarketplaceCartItemType["Count"],
  ) => Promise<MarketplaceCartType>;
  updateCartShipTo: (
    shipTo: MarketplaceCartShipToType,
  ) => Promise<MarketplaceCartType>;
  fetchCartShipment: (
    shipment: MarketplaceCartShipmentDataType,
  ) => Promise<MarketplaceCartShipmentItemType[]>;
  updateCartShipment: (
    shipment: MarketplaceCartShipmentDataType,
  ) => Promise<MarketplaceCartType>;

  fetchCartPaymentLink: () => Promise<string>;
  fetchCartStatus: (sessionId: string) => Promise<0 | 1>;
}

export const serviceMarketplaceCartApi: ServiceMarketplaceCartApiType = {
  fetchCartRequiredProducts: () => {
    return axios
      .get(`${env.services.portal}/store/cart/required`)
      .then((response: AxiosResponse<unknown[]>) => {
        return (response.data || []).map((item) =>
          ProductItemSchema.parse(item),
        );
      });
  },
  fetchCartRecommendedProducts: () => {
    return axios
      .get(`${env.services.portal}/store/cart/recommended`)
      .then((response: AxiosResponse<unknown[]>) => {
        return (response.data || []).map((item) =>
          ProductItemSchema.parse(item),
        );
      });
  },
  fetchUpdateShoppingItemCart: (item) => {
    return axios
      .patch(`${env.services.portal}/store/cart/items/${item.Id}`, item)
      .then((response: AxiosResponse<unknown>) => {
        return MarketplaceCartSchema.parse(response.data || {});
      });
  },
  fetchMarketplaceCart: () => {
    return axios
      .get(`${env.services.portal}/store/cart`)
      .then((response: AxiosResponse<unknown>) => {
        return MarketplaceCartSchema.parse(response.data || {});
      });
  },
  fetchRemoveProduct: (itemId) => {
    return axios
      .delete(`${env.services.portal}/store/cart/items/${itemId}`)
      .then((response: AxiosResponse<unknown>) => {
        return MarketplaceCartSchema.parse(response.data || {});
      });
  },
  fetchAddProductToCart: (CostId, Count) => {
    return axios
      .post(`${env.services.portal}/store/cart/items`, {
        CostId,
        Quantity: Count || 1,
      })
      .then((response: AxiosResponse<unknown>) => {
        return MarketplaceCartSchema.parse(response.data || {});
      });
  },
  updateCartShipTo: (shipTo) => {
    return axios
      .post(`${env.services.portal}/store/cart/shipto`, shipTo)
      .then((response: AxiosResponse<unknown>) => {
        return MarketplaceCartSchema.parse(response.data || {});
      });
  },
  fetchCartShipment: (shipment) => {
    return axios
      .post(`${env.services.portal}/store/shipment`, shipment)
      .then((response: AxiosResponse<unknown[]>) => {
        return (response.data || []).map((item) =>
          MarketplaceCartShipmentItemSchema.parse(item),
        );
      });
  },
  updateCartShipment: (shipment) => {
    return axios
      .post(`${env.services.portal}/store/cart/shipment`, shipment)
      .then((response: AxiosResponse<unknown>) => {
        return MarketplaceCartSchema.parse(response.data || {});
      });
  },
  fetchCartPaymentLink: () => {
    return axios
      .get(`${env.services.portal}/store/cart/paymentlink`)
      .then((response: AxiosResponse<unknown>) => {
        return response.data as string;
      });
  },
  fetchCartStatus: (sessionId: string) => {
    return axios
      .get(`${env.services.portal}/store/cart/status`, {
        params: { session_id: sessionId },
      })
      .then((response: AxiosResponse<unknown>) => {
        return response.data as 0 | 1;
      });
  },
};
